'use strict'

import React, { useCallback } from 'react'
import { ChrofyShowErrors } from '../../../common/Form'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'

const FORM_TITLE = '単純マッピング'
const ERRORS = {
  FILE_TYPE_REQUIRED: 'ファイル種別を選択してください',
  COLUMN_NAME_REQUIRED: '項目名を選択してください'
}

/**
 * @typedef {{ MappingFileType?: string; MappingColumnName?: string }} SimpleMapping
 *
 * @typedef {{ MappingType: 'SimpleMapping'; SimpleMapping: SimpleMapping }} MappingInfoSimple
 */

/**
 * 単純マッピングフォーム本体（設定エリア・エラー表示）
 *
 * @param {{
 *   fileTypes: import('../extra/types').FileTypeInfo[]
 *   initialMappingInfo: MappingInfoSimple | null
 *   formData: { mappingFileType: string; mappingColumnName: string }
 *   errors: Record<string, string[]>
 *   onChange: (
 *     fd: { mappingFileType: string; mappingColumnName: string },
 *     setErrors: (e: Record<string, string[]>) => void
 *   ) => void
 * }} props
 */
export const SimpleMappingForm = (props) => {
  const { fileTypes = [], initialMappingInfo, formData, errors, onChange } = props

  const selectedFile = fileTypes.find((f) => f.mappingFileType === formData.mappingFileType)
  const columnOptions = (selectedFile?.headers ?? []).map((h) => ({ label: h, value: h }))
  const isColumnDisabled = !formData.mappingFileType

  const handleFileTypeChange = useCallback(
    (e) => {
      const mappingFileType = e.target.value ?? ''
      onChange({ mappingFileType, mappingColumnName: '' }, () => {})
    },
    [onChange]
  )

  const handleColumnChange = useCallback(
    (e) => {
      const mappingColumnName = e.target.value ?? ''
      onChange({ ...formData, mappingColumnName }, () => {})
    },
    [formData, onChange]
  )

  return (
    <div className="text-h7">
      <div className="form-table">
        <table className="w-full">
          <tbody>
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label className="text-h7 font-bold">
                  ファイル種別 <span className="red--text">*</span>
                </label>
              </th>
              <td className="form-table__col-data">
                <ChrofyCustomSelect
                  id="simple-mapping-file-type"
                  name="simple-mapping-file-type"
                  value={formData.mappingFileType}
                  onChange={handleFileTypeChange}
                  className="w-full"
                  options={(fileTypes || []).map((f) => ({ label: f.mappingFileType, value: f.mappingFileType }))}
                  disabled={false}
                  hasNoSelect={true}
                  searchable
                  isError={Boolean(errors.mappingFileType?.length)}
                  data-testid="simple-mapping-file-type"
                />
                <ChrofyShowErrors field="mappingFileType" errors={errors} />
              </td>
            </tr>
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label className="text-h7 font-bold">
                  項目名 <span className="red--text">*</span>
                </label>
              </th>
              <td className="form-table__col-data">
                <ChrofyCustomSelect
                  id="simple-mapping-column-name"
                  name="simple-mapping-column-name"
                  value={formData.mappingColumnName}
                  onChange={handleColumnChange}
                  className={isColumnDisabled ? 'opacity-60 w-full' : 'w-full'}
                  options={columnOptions}
                  disabled={isColumnDisabled}
                  hasNoSelect={true}
                  searchable
                  isError={Boolean(errors.mappingColumnName?.length)}
                  data-testid="simple-mapping-column-name"
                />
                <ChrofyShowErrors field="mappingColumnName" errors={errors} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
